import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API = axios.create({ baseURL: '/api', withCredentials: true });

const STATUS_META = {
  not_paid:  { label: 'Not Paid',       dot: '#dc2626' },
  partial:   { label: 'Partially Paid', dot: '#F5A800' },
  paid_full: { label: 'Paid in Full',   dot: '#15803D' },
};

const InvoiceSearch = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    invoice_number: '',
    date_from: '',
    date_to: ''
  });

  const showStatus = user?.role === 'boss' || user?.role === 'admin';

  useEffect(() => {
    const stored = localStorage.getItem('selected_customer');
    if (!stored) { navigate('/select-customer'); return; }
    const c = JSON.parse(stored);
    setCustomer(c);
    fetchInvoices(c.customer_id, {});
  }, [navigate]);

  const fetchInvoices = async (customerId, f) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ customer_id: customerId });
      if (f.invoice_number) params.append('invoice_number', f.invoice_number);
      if (f.date_from) params.append('date_from', f.date_from);
      if (f.date_to) params.append('date_to', f.date_to);
      const res = await API.get(`/invoices?${params}`);
      setInvoices(res.data);
    } catch {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (customer) fetchInvoices(customer.customer_id, filters);
  };

  const handleClear = () => {
    setFilters({ invoice_number: '', date_from: '', date_to: '' });
    if (customer) fetchInvoices(customer.customer_id, {});
  };

  const inputStyle = {
    padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: '6px',
    fontSize: '14px', outline: 'none'
  };

  const headers = showStatus
    ? ['Status', 'Invoice #', 'Date', 'Bill To', 'Shipments', 'Amount Due', 'Actions']
    : ['Invoice #', 'Date', 'Bill To', 'Shipments', 'Amount Due', 'Actions'];

  return (
    <div className="fcc-fade-in" style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '600' }}>Invoices</h1>
          {customer && <p style={{ color: '#5B6B7F', fontSize: '14px' }}>Customer: <strong>{customer.name}</strong></p>}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => navigate('/dashboard')} className="fcc-press" style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}>
            ← Dashboard
          </button>
          <button onClick={() => navigate('/invoices/new')} className="btn btn-primary fcc-press" style={{ width: 'auto', padding: '8px 16px' }}>
            + New Invoice
          </button>
        </div>
      </div>

      {/* Search filters */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '16px 20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Invoice #</label>
            <input
              style={{ ...inputStyle, width: '160px' }}
              placeholder="INV-00001"
              value={filters.invoice_number}
              onChange={e => setFilters(prev => ({ ...prev, invoice_number: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>From Date</label>
            <input
              type="date"
              style={{ ...inputStyle, width: '160px' }}
              value={filters.date_from}
              onChange={e => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>To Date</label>
            <input
              type="date"
              style={{ ...inputStyle, width: '160px' }}
              value={filters.date_to}
              onChange={e => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
            />
          </div>
          <button onClick={handleSearch} className="btn btn-primary fcc-press" style={{ width: 'auto', padding: '8px 20px' }}>
            Search
          </button>
          <button onClick={handleClear} className="fcc-press" style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}>
            Clear
          </button>
        </div>
      </div>

      {/* Results table */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #E2E8F0' }}>
          <p style={{ fontSize: '14px', color: '#5B6B7F' }}>
            {loading ? 'Loading...' : `${invoices.length} invoice${invoices.length !== 1 ? 's' : ''} found`}
          </p>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr>
              {headers.map(h => (
                <th key={h} style={{
                  padding: '10px 12px', background: '#1A2A3A', color: 'white',
                  fontSize: '13px', fontWeight: '500', textAlign: h === 'Status' ? 'center' : 'left',
                  borderRight: '1px solid #F5A800',
                  width: h === 'Status' ? '70px' : undefined
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan={headers.length} style={{ padding: '40px', textAlign: 'center', color: '#5B6B7F' }}>
                  No invoices found
                </td>
              </tr>
            ) : (
              invoices.map(inv => {
                const meta = STATUS_META[inv.payment_status] || STATUS_META.not_paid;
                return (
                  <tr key={inv.invoice_id} className="fcc-row-hover" style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/invoices/${inv.invoice_id}`)}
                  >
                    {showStatus && (
                      <td style={{ padding: '12px', borderBottom: '1px solid #E2E8F0', textAlign: 'center' }}>
                        <span
                          title={meta.label}
                          style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', background: meta.dot }}
                        />
                      </td>
                    )}
                    <td style={{ padding: '12px', borderBottom: '1px solid #E2E8F0', fontWeight: '600', color: '#F5A800' }}>
                      {inv.invoice_number}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #E2E8F0' }}>
                      {new Date(inv.invoice_date).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #E2E8F0' }}>{inv.bill_to}</td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #E2E8F0', color: '#5B6B7F' }}>—</td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #E2E8F0', fontWeight: '600', color: '#15803D' }}>
                      ${parseFloat(inv.amount_due).toFixed(2)}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #E2E8F0' }}>
                      <button
                        onClick={e => { e.stopPropagation(); navigate(`/invoices/${inv.invoice_id}`); }}
                        className="fcc-press"
                        style={{ background: '#F5A800', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', marginRight: '6px' }}
                      >
                        View
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); navigate(`/invoices/${inv.invoice_id}/edit`); }}
                        className="fcc-press"
                        style={{ background: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoiceSearch;